import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { LeadWithRelations } from '@/types';
import { LEAD_STAGES, canMoveLead, stageOf, type LeadStage } from '@/components/leads/leadStages';
import { useLeadStageMove } from '@/hooks/useLeadStageMove';
import { LeadsBoardColumn } from './LeadsBoardColumn';
import { LeadsBoardCard } from './LeadsBoardCard';

interface LeadsBoardProps {
  leadsByStage: Record<LeadStage, LeadWithRelations[]>;
  onLeadClick: (id: string) => void;
  selectedLeadId?: string | null;
}

export function LeadsBoard({ leadsByStage, onLeadClick, selectedLeadId }: LeadsBoardProps) {
  const { requestMove, dialogs } = useLeadStageMove();
  const [activeLead, setActiveLead] = useState<LeadWithRelations | null>(null);
  const [lostCollapsed, setLostCollapsed] = useState(true);

  // Mouse drags after 8px so a click still opens the lead; touch waits for a
  // short press so the board can still be scrolled with a finger.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveLead((event.active.data.current?.lead as LeadWithRelations | undefined) ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const lead = event.active.data.current?.lead as LeadWithRelations | undefined;
    setActiveLead(null);
    if (!lead || !event.over) return;
    requestMove(lead, event.over.id as LeadStage);
  };

  const activeStage = activeLead ? stageOf(activeLead.status) : null;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveLead(null)}
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          {LEAD_STAGES.map((stage) => (
            <LeadsBoardColumn
              key={stage.value}
              stage={stage}
              leads={leadsByStage[stage.value]}
              dragging={!!activeLead}
              accepts={!!activeStage && canMoveLead(activeStage, stage.value)}
              collapsed={stage.value === 'lost' && lostCollapsed}
              onToggleCollapsed={stage.value === 'lost' ? () => setLostCollapsed((c) => !c) : undefined}
              onOpenLead={onLeadClick}
              onMove={requestMove}
              selectedLeadId={selectedLeadId}
            />
          ))}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeLead ? <LeadsBoardCard lead={activeLead} overlay /> : null}
        </DragOverlay>
      </DndContext>

      {dialogs}
    </>
  );
}
